// Single check screen (spec §7.1.1): inputs with meters, summary, previews grouped by device class.
// Basecoat cards, fields, an accordion for the optional fields, and tabs for the device classes.
import { browserEngine, normalize, codePointLength, isErrorField, type FieldResult, type Profile, type Status } from '../engine';
import { alert, clear, h, icon, ICONS, rafBatch, refreshComponent } from './dom';
import { badge, deviceFrame, stripLegend, warningChip } from './preview';
import { highlighter, type Highlighter } from './highlight';
import { followScroll } from './sticky';
import { CLASS_LABEL, CLASS_ORDER, enabledProfiles, onSettings, saveDraft, state, type Draft } from './state';
import { track } from './track';

/** Previews update on every keystroke, so a check counts for analytics once typing pauses this long. */
const CHECK_IDLE_MS = 2000;

export function checkView(): HTMLElement {
  const d = state.draft;
  const highlights: Partial<Record<keyof Draft, Highlighter>> = {};
  const field = (key: keyof Draft, label: string, opts: { multiline?: boolean; placeholder?: string; hint?: string; highlight?: boolean } = {}) => {
    const id = `in-${key}`;
    const input = opts.multiline
      ? h('textarea', { id, rows: 4, placeholder: opts.placeholder ?? '', spellcheck: true })
      : h('input', { id, type: key === 'url' ? 'url' : 'text', placeholder: opts.placeholder ?? '', autocomplete: 'off', spellcheck: key !== 'url' });
    input.value = d[key];
    input.addEventListener('input', () => { saveDraft({ [key]: input.value }); schedule(); reportSoon(); });
    const hl = opts.highlight ? highlighter(input as HTMLTextAreaElement, () => state.config) : null;
    if (hl) highlights[key] = hl;
    if (opts.hint) input.setAttribute('aria-describedby', `${id}-hint`);
    return { input, el: h('div', { class: 'field' }, h('label', { for: id }, label), hl ? hl.el : input, opts.hint ? h('p', { id: `${id}-hint` }, opts.hint) : null) };
  };

  const title = field('title', 'Title', { multiline: true, placeholder: 'Page title tag', highlight: true });
  (title.input as HTMLTextAreaElement).rows = 2;
  const desc = field('description', 'Description', { multiline: true, placeholder: 'Meta description', highlight: true });
  const url = field('url', 'URL', { placeholder: 'https://example.com/page', hint: 'Shown in the preview’s site line.' });
  const brand = field('brand', 'Brand', { placeholder: state.settings.brand || 'e.g. Example', hint: 'Warns when the brand is hidden.' });
  const keyword = field('keyword', 'Target keyword', { hint: 'Warns when the keyword is not fully shown.' });
  const query = field('query', 'Search query', { hint: 'Bolds matching words in descriptions, like Google does.' });

  const titleMeter = h('div', { class: 'meter', role: 'group', 'aria-label': 'Title width' });
  const descMeter = h('div', { class: 'meter', role: 'group', 'aria-label': 'Description length' });
  const titleNote = h('p', { class: 'cut-note', 'aria-live': 'polite' });
  const descNote = h('p', { class: 'cut-note', 'aria-live': 'polite' });

  const more = h('details', { class: 'more' },
    h('summary', null, 'More: URL, brand, keyword, query', icon(ICONS.chevron, 16)),
    h('section', null, url.el, brand.el, keyword.el, query.el));
  if (d.keyword || d.query) more.open = true;

  const summaryBody = h('section', { 'aria-live': 'polite' });
  const summary = h('section', { class: 'card summary', 'aria-labelledby': 'summary-title' },
    h('header', null, h('h2', { id: 'summary-title' }, 'Summary')), summaryBody);
  const notices = h('div', { class: 'notices' });
  const canvas = h('div', { class: 'canvas' });
  // Basecoat tabs: its script handles clicks, arrow keys, Home / End and shows the chosen band
  // (found by aria-controls). Each render rebuilds the tabs, then refreshComponent() re-syncs it.
  const seg = h('nav', { role: 'tablist', 'aria-label': 'Device class', 'aria-orientation': 'horizontal' });
  const segBar = h('div', { class: 'seg-bar tabs' }, seg);
  const bands = h('div', { class: 'bands' });
  canvas.append(segBar, notices, bands);

  const left = h('div', { class: 'check-left' },
    h('section', { class: 'card inputs', 'aria-label': 'Title and description' },
      h('section', null,
        h('div', { class: 'field-group' }, title.el, titleMeter, titleNote),
        h('div', { class: 'field-group' }, desc.el, descMeter, descNote),
        h('div', { class: 'accordion' }, more))),
    summary);

  const root = h('div', { class: 'check' }, left, canvas);
  // The rail sticks below the 56px top bar with a 20px margin; one column below 1100px (styles.css), where it doesn't stick.
  followScroll(left, { top: 76, gap: 20, media: '(min-width: 1100px)' });
  const dismissed = new Set<string>();

  function meter(el: HTMLElement, value: number, max: number, unit: string, status: Status | null) {
    const pct = Math.min(100, (value / max) * 100);
    clear(el,
      h('div', { class: 'meter-bar' }, h('div', { class: `meter-fill fill-${status ?? 'none'}`, style: { width: `${pct}%` } })),
      h('div', { class: 'meter-row' },
        status ? badge(status) : h('span', { class: 'meter-empty' }, 'Empty'),
        h('span', { class: 'mono meter-read' }, `${unit === 'px' ? value.toFixed(0) : value} / ${max} ${unit}`)));
  }

  function cutNote(el: HTMLElement, f: FieldResult | undefined, hl: Highlighter | undefined) {
    const parts = hl?.update(f) ?? { limit: '', google: '' };
    const n = f?.results.length ?? 0;
    const hiddenOn = f ? f.results.filter(r => r.cutBy === 'lines' || r.cutBy === 'both').length : 0;
    clear(el,
      parts.limit ? h('span', null, h('mark', { class: 'hl-limit' }, 'Hidden on ', hiddenOn === n ? 'every device' : `${hiddenOn} of ${n} devices`), ' by the line limit') : null,
      parts.limit && parts.google ? h('span', null, ' · ') : null,
      parts.google ? h('span', null, h('mark', { class: 'hl-google' }, 'Cut by Google'), ' on every device') : null);
    el.hidden = !parts.limit && !parts.google;
  }

  function fieldSummary(f: FieldResult | undefined, name: string) {
    if (!f) return h('div', { class: 'sum-row' }, h('span', { class: 'sum-k' }, name), h('span', { class: 'muted' }, 'Empty'));
    const n = f.results.length;
    const k = f.results.filter(r => r.status === f.overall).length;
    const scope = k === n ? 'on all devices' : k === 1 ? 'on 1 device' : `on ${k} of ${n} devices`;
    const fieldWarn = f.warnings.filter(w => w.code !== 'TIGHT');
    return h('div', { class: 'sum-row' },
      h('span', { class: 'sum-k' }, name),
      h('div', { class: 'sum-v' }, h('div', { class: 'sum-status' }, badge(f.overall), h('span', { class: 'muted' }, scope)),
        fieldWarn.length ? h('div', { class: 'chips' }, fieldWarn.map(warningChip)) : null));
  }

  function notice(id: string, text: string) {
    if (dismissed.has(id)) return null;
    const btn = h('button', { class: 'icon-btn', type: 'button', 'aria-label': 'Dismiss' }, icon(ICONS.close, 14));
    const el = alert(text, undefined, btn);
    btn.addEventListener('click', () => { dismissed.add(id); el.remove(); });
    return el;
  }

  // Latest results, and the draft last reported, so an unchanged draft is never counted twice.
  let latest: { t?: FieldResult; ds?: FieldResult; devices: number } = { devices: 0 };
  let reported = '';
  let idle = 0;
  function reportSoon() {
    clearTimeout(idle);
    idle = window.setTimeout(() => {
      const { t, ds, devices } = latest;
      const key = JSON.stringify(state.draft);
      if ((!t && !ds) || key === reported) return;
      reported = key;
      const codes = new Set([t, ds].flatMap(f => f?.warnings.map(w => w.code) ?? []).filter(c => c !== 'TIGHT'));
      track('snippet_check', {
        title_status: t?.overall ?? 'empty',
        description_status: ds?.overall ?? 'empty',
        warnings: [...codes].sort().join(',') || 'none',
        device_count: devices,
      });
    }, CHECK_IDLE_MS);
  }

  function render() {
    const dr = state.draft;
    const profiles = enabledProfiles();
    const b = normalize(dr.brand) || state.settings.brand;
    const results = state.checker.checkPage({ url: dr.url, title: dr.title, description: dr.description, targetKeyword: dr.keyword, query: dr.query }, 0, profiles, b);
    const ok = results.filter(r => !isErrorField(r));
    const t = ok.find(r => r.field === 'title');
    const ds = ok.find(r => r.field === 'description');
    latest = { t, ds, devices: profiles.length };
    const cfg = state.config;
    const tol = 1 + cfg.overflowTolerancePct / 100;

    const tw = t?.googleCut.widthPx ?? 0;
    meter(titleMeter, tw, cfg.titleLimitPx, 'px', !t ? null : tw > cfg.titleLimitPx * tol ? 'cut' : tw > cfg.titleLimitPx - cfg.tightTitlePx ? 'tight' : 'fits');
    const chars = codePointLength(normalize(dr.description));
    meter(descMeter, chars, cfg.descCharCap, 'chars', !ds ? null : chars > cfg.descCharCap ? 'cut' : chars > cfg.descCharCap - 10 ? 'tight' : 'fits');
    cutNote(titleNote, t, highlights.title);
    cutNote(descNote, ds, highlights.description);

    clear(summaryBody, fieldSummary(t, 'Title'), fieldSummary(ds, 'Description'),
      ds?.results.some(r => r.bolded) ? h('p', { class: 'hint' }, 'Query terms are bolded in previews. Bolding is approximate: Google’s exact rules are unknown.') : null);

    const eng = browserEngine();
    const foreign = profiles.some(p => p.engine !== eng);
    const fell = ok.some(f => f.results.some(r => r.fontFallback?.length));
    clear(notices,
      foreign ? notice('engine', `This browser measures every device with its own text engine. The command-line runner measures iPhone and iPad in WebKit and the rest in Chromium, so a word right at a boundary can differ.`) : null,
      fell ? notice('fallback', 'A font some profiles need (such as Helvetica) is not installed in this browser, so a fallback font measured them. Those previews are marked “Font fallback”.') : null);

    const present = CLASS_ORDER.filter(c => profiles.some(p => p.deviceClass === c));
    // Keep the band the person chose (Basecoat marks it aria-selected) across re-renders.
    const chosen = seg.querySelector<HTMLElement>('[aria-selected="true"]')?.dataset.class;
    const current = present.find(c => c === chosen) ?? present[0];
    clear(seg, present.map(c => {
      const n = profiles.filter(p => p.deviceClass === c).length;
      return h('button', { type: 'button', role: 'tab', id: `tab-${c}`, 'aria-selected': String(c === current), tabindex: c === current ? '0' : '-1', 'aria-controls': `band-${c}`, 'data-class': c },
        CLASS_LABEL[c], h('span', { class: 'seg-count mono' }, String(n)));
    }));

    if (!t && !ds) {
      refreshComponent(segBar);
      clear(bands, h('div', { class: 'empty' }, h('header', null, h('h2', null, 'Type a title or description'), h('p', null, 'Previews for each device appear here as you type.'))));
      return;
    }
    let legendShown = false;
    clear(bands, present.map(c => {
      const ps = profiles.filter(p => p.deviceClass === c);
      return h('section', { class: 'band', id: `band-${c}`, role: 'tabpanel', 'data-class': c, 'aria-labelledby': `tab-${c}`, hidden: c !== current },
        h('h2', { class: 'band-title' }, CLASS_LABEL[c], h('span', { class: 'muted mono' }, ` ${ps.length}`)),
        h('div', { class: 'frames' }, ps.map((p: Profile) => {
          const fr = deviceFrame({ profile: p, title: t, description: ds, url: dr.url, brand: b, query: dr.query, showHidden: state.settings.showHidden, config: cfg });
          if (!legendShown && fr.querySelector('.strip s')) { fr.querySelector('.frame-foot')!.insertBefore(stripLegend(), fr.querySelector('.frame-meta')); legendShown = true; }
          return fr;
        })));
    }));
    refreshComponent(segBar);
  }

  const schedule = rafBatch(render);
  onSettings(schedule);
  render();
  return root;
}
